import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileUp,
  Loader2,
  Plus,
  SearchCheck,
  X,
} from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../contexts/ToastContext";

const EMPTY = {
  occurred_at: "",
  inspection_mode: "routine",
  cro_state: "",
  fiscal_name: "",
  protocol: "",
  received_by: "",
  regularization_deadline: "",
  reinspection_at: "",
  notes: "",
};
const MODES = {
  routine: "Rotina",
  complaint: "Denúncia",
  reinspection: "Reinspeção",
  orientative: "Orientativa",
  special: "Especial",
  joint: "Conjunta",
  online: "Online",
  other: "Outra",
};
const date = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

export default function ReceivedInspectionsPanel({ canManage }) {
  const { showToast } = useToast();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [termTarget, setTermTarget] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/compliance/received-inspections");
      setData(r.data);
    } catch (e) {
      showToast(
        e?.response?.data?.detail ||
          "Não foi possível carregar as fiscalizações.",
        "error",
      );
    }
  };
  useEffect(() => {
    load();
  }, []);
  const create = async () => {
    setSaving(true);
    try {
      await api.post("/compliance/received-inspections", {
        ...form,
        cro_state: form.cro_state || null,
        fiscal_name: form.fiscal_name || null,
        protocol: form.protocol || null,
        regularization_deadline: form.regularization_deadline || null,
        reinspection_at: form.reinspection_at || null,
        notes: form.notes || null,
      });
      setForm(null);
      await load();
      showToast("Fiscalização registrada.");
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Não foi possível registrar.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };
  const addAction = async () => {
    setSaving(true);
    try {
      const r = await api.post(
        `/compliance/received-inspections/${selected.id}/actions`,
        {
          ...action,
          responsible: action.responsible || null,
          due_at: action.due_at || null,
        },
      );
      setSelected(r.data);
      setAction(null);
      await load();
      showToast("Ação incluída.");
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Não foi possível incluir a ação.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };
  const updateAction = async (item, status) => {
    try {
      const r = await api.patch(
        `/compliance/received-inspections/${selected.id}/actions/${item.id}`,
        { status, completion_note: null },
      );
      setSelected(r.data);
      await load();
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Não foi possível atualizar.",
        "error",
      );
    }
  };
  const uploadTerm = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !termTarget) return;
    const body = new FormData();
    body.append("file", file);
    setSaving(true);
    try {
      await api.post(
        `/compliance/received-inspections/${termTarget.id}/term`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      await load();
      if (selected?.id === termTarget.id)
        setSelected({ ...selected, term: { original_filename: file.name } });
      showToast("Termo incluído.");
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Não foi possível enviar o Termo.",
        "error",
      );
    } finally {
      setSaving(false);
      setTermTarget(null);
    }
  };
  const openTerm = async (item) => {
    const r = await api.get(
      `/compliance/received-inspections/${item.id}/term/file`,
      { responseType: "blob" },
    );
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const close = async () => {
    try {
      const r = await api.patch(
        `/compliance/received-inspections/${selected.id}/close`,
      );
      setSelected(r.data);
      await load();
      showToast("Fiscalização encerrada.");
    } catch (e) {
      showToast(
        e?.response?.data?.detail || "Não foi possível encerrar.",
        "error",
      );
    }
  };
  const formDirty = Boolean(
    form &&
      Object.keys(EMPTY).some(
        (key) => String(form[key] || "") !== String(EMPTY[key] || ""),
      ),
  );
  const actionDirty = Boolean(
    action &&
      [action.finding, action.action, action.responsible, action.due_at].some(
        (value) => String(value || "").trim(),
      ),
  );
  const confirmDiscard = () =>
    window.confirm("Existem alterações não salvas. Deseja descartá-las?");
  const closeForm = () => {
    if (saving || (formDirty && !confirmDiscard())) return;
    setForm(null);
  };
  const closeAction = () => {
    if (saving || (actionDirty && !confirmDiscard())) return;
    setAction(null);
  };
  if (!data) return null;
  return (
    <section className="rounded-2xl bg-white shadow-sm">
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        onChange={uploadTerm}
      />
      <div className="p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div className="flex gap-3">
            <span className="rounded-xl bg-slate-100 p-3 text-slate-700">
              <SearchCheck className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-semibold text-gray-900">
                Fiscalizações recebidas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Guarde o Termo e acompanhe achados, responsáveis, prazos e
                reinspeção.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {data.overdue > 0 && (
              <span className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                {data.overdue} vencida(s)
              </span>
            )}
            {canManage && (
              <button
                onClick={() => setForm({ ...EMPTY })}
                className="flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white"
              >
                <Plus className="h-4 w-4" /> Registrar fiscalização
              </button>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between border-t px-5 py-4 text-sm font-medium text-gray-700"
      >
        <span>
          {data.total
            ? `${data.total} registro(s) · ${data.open} em acompanhamento`
            : "Nenhuma fiscalização registrada"}
        </span>
        {expanded ? (
          <ChevronUp className="h-5 w-5" />
        ) : (
          <ChevronDown className="h-5 w-5" />
        )}
      </button>
      {expanded && (
        <div className="divide-y border-t">
          {data.inspections.map((item) => (
            <button
              key={item.id}
              onClick={() => setSelected(item)}
              className="flex w-full items-center justify-between gap-3 p-4 text-left sm:px-6"
            >
              <div>
                <p className="font-medium text-gray-900">
                  {MODES[item.inspection_mode]} · {date(item.occurred_at)}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {item.protocol ? `Protocolo ${item.protocol} · ` : ""}
                  {item.pending_actions} ação(ões) pendente(s)
                </p>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs ${item.attention_status === "overdue" ? "bg-red-100 text-red-700" : item.status === "closed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}
              >
                {item.status === "closed"
                  ? "Encerrada"
                  : item.attention_status === "overdue"
                    ? "Prazo vencido"
                    : "Em acompanhamento"}
              </span>
            </button>
          ))}
        </div>
      )}
      {form && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <div className="mx-auto mt-8 max-w-2xl rounded-2xl bg-white p-5">
            <div className="flex justify-between">
              <h3 className="font-semibold">Registrar fiscalização</h3>
              <button onClick={closeForm}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                Data e hora
                <input
                  type="datetime-local"
                  value={form.occurred_at}
                  onChange={(e) =>
                    setForm({ ...form, occurred_at: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="text-sm">
                Tipo
                <select
                  value={form.inspection_mode}
                  onChange={(e) =>
                    setForm({ ...form, inspection_mode: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                >
                  {Object.entries(MODES).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                Quem recebeu
                <input
                  value={form.received_by}
                  onChange={(e) =>
                    setForm({ ...form, received_by: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="text-sm">
                Fiscal
                <input
                  value={form.fiscal_name}
                  onChange={(e) =>
                    setForm({ ...form, fiscal_name: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="text-sm">
                Protocolo
                <input
                  value={form.protocol}
                  onChange={(e) =>
                    setForm({ ...form, protocol: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
              <label className="text-sm">
                Prazo para regularização
                <input
                  type="datetime-local"
                  value={form.regularization_deadline}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      regularization_deadline: e.target.value,
                    })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                />
              </label>
            </div>
            <button
              disabled={saving || !form.occurred_at || !form.received_by}
              onClick={create}
              className="mt-5 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? "Salvando..." : "Salvar fiscalização"}
            </button>
          </div>
        </div>
      )}
      {selected && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelected(null);
          }}
        >
          <div className="mx-auto my-8 max-w-3xl rounded-2xl bg-white p-5">
            <div className="flex justify-between">
              <div>
                <h3 className="font-semibold">
                  {MODES[selected.inspection_mode]} ·{" "}
                  {date(selected.occurred_at)}
                </h3>
                <p className="text-xs text-gray-500">
                  {selected.protocol || "Sem protocolo informado"}
                </p>
              </div>
              <button onClick={() => setSelected(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {selected.term ? (
                <button
                  onClick={() => openTerm(selected)}
                  className="flex items-center gap-1 rounded-lg border px-3 py-2 text-xs"
                >
                  <Download className="h-4 w-4" /> Abrir Termo
                </button>
              ) : (
                canManage && (
                  <button
                    onClick={() => {
                      setTermTarget(selected);
                      fileRef.current?.click();
                    }}
                    className="flex items-center gap-1 rounded-lg border px-3 py-2 text-xs"
                  >
                    <FileUp className="h-4 w-4" /> Incluir Termo
                  </button>
                )
              )}
            </div>
            <div className="mt-5 space-y-2">
              {selected.actions?.map((item) => (
                <div key={item.id} className="rounded-xl border p-3">
                  <p className="text-sm font-medium">{item.finding}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Ação: {item.action}
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {item.responsible || "Sem responsável"} · prazo{" "}
                      {date(item.due_at)}
                    </span>
                    {canManage && (
                      <select
                        value={item.status}
                        onChange={(e) => updateAction(item, e.target.value)}
                        className="rounded border p-1 text-xs"
                      >
                        <option value="pending">Pendente</option>
                        <option value="in_progress">Em andamento</option>
                        <option value="completed">Concluída</option>
                        <option value="not_applicable">Não se aplica</option>
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {canManage && selected.status !== "closed" && (
              <>
                <button
                  onClick={() =>
                    setAction({
                      finding: "",
                      action: "",
                      responsible: "",
                      due_at: "",
                    })
                  }
                  className="mt-4 flex items-center gap-1 text-sm font-semibold text-indigo-700"
                >
                  <Plus className="h-4 w-4" /> Adicionar achado e ação
                </button>
                {selected.actions?.length > 0 &&
                  selected.pending_actions === 0 && (
                    <button
                      onClick={close}
                      className="mt-4 ml-3 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                    >
                      Encerrar fiscalização
                    </button>
                  )}
              </>
            )}
            {selected.attention_status === "overdue" && (
              <p className="mt-4 flex gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-700">
                <AlertTriangle className="h-4 w-4" /> Prazo vencido com ações
                pendentes.
              </p>
            )}
          </div>
        </div>
      )}
      {action && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeAction();
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5">
            <h3 className="font-semibold">Novo achado</h3>
            <textarea
              placeholder="O que foi apontado"
              value={action.finding}
              onChange={(e) =>
                setAction({ ...action, finding: e.target.value })
              }
              className="mt-3 w-full rounded-lg border p-2"
            />
            <textarea
              placeholder="Ação para regularizar"
              value={action.action}
              onChange={(e) => setAction({ ...action, action: e.target.value })}
              className="mt-3 w-full rounded-lg border p-2"
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input
                placeholder="Responsável"
                value={action.responsible}
                onChange={(e) =>
                  setAction({ ...action, responsible: e.target.value })
                }
                className="rounded-lg border p-2"
              />
              <label className="text-xs font-medium text-gray-600">
                Prazo da ação <span className="font-normal text-gray-400">(opcional)</span>
                <input
                  type="datetime-local"
                  value={action.due_at}
                  onChange={(e) =>
                    setAction({ ...action, due_at: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2 text-sm text-gray-800"
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeAction}
                className="px-3 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                disabled={saving || !action.finding || !action.action}
                onClick={addAction}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Salvar ação
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
